import { z } from "zod";

/**
 * **Las reglas de la cuenta, en un solo archivo que usan el formulario y el servidor.**
 *
 * Es el patrón del ecosistema (ver `Vecipp-Auth`): el mismo módulo se importa en el `page.tsx` con
 * `zodResolver` y en el route handler con `safeParse`, así no hay forma de que la validación del
 * navegador y la del servidor se desincronicen. Este archivo es puro: no importa `db` ni nada de
 * servidor, por eso puede cruzar la frontera.
 *
 * ⚠️ **La contraseña acá pide MENOS que en las apps de negocio, y es deliberado.** Allá el checklist
 * exige mayúscula, número y símbolo porque quien entra maneja la contabilidad de una empresa. Acá entra
 * un vecino a pedir algo en la tienda de la esquina, muchas veces desde un celular de gama baja: cada
 * requisito extra es gente que abandona el registro. 8 caracteres y que no sea su propio correo.
 */

export const MIN_PASSWORD = 8;

const email = z
  .string()
  .trim()
  .min(1, "Escribe tu correo.")
  .email("Ese correo no parece válido.")
  .max(255, "Ese correo es demasiado largo.");

const password = z
  .string()
  .min(MIN_PASSWORD, `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`)
  .max(72, "La contraseña es demasiado larga."); // 72 = el límite real de bcrypt, no un número al azar

const nombre = z.string().trim().min(2, "Escribe tu nombre.").max(120, "Ese nombre es demasiado largo.");

export const registroSchema = z
  .object({
    nombre,
    email,
    password,
    telefono: z.string().trim().max(30, "Ese teléfono es demasiado largo.").optional().or(z.literal("")),
    idMunicipio: z.coerce.number().int().positive().optional(),
  })
  .refine((d) => d.password.toLowerCase() !== d.email.toLowerCase(), {
    message: "La contraseña no puede ser tu propio correo.",
    path: ["password"],
  });

/**
 * **Cambiar mis datos** (`PATCH /api/cuenta/datos`, 2026-10-28): el nombre con la regla del registro y el celular con la
 * de la verificación para trabajar (`Vecipp-Barrio-Core/lib/verificacion.ts`: de 7 a 13 dígitos, con «+» si se quiere;
 * los espacios y guiones se quitan). El teléfono vacío o `null` lo quita. Los dos son opcionales, pero tiene que llegar
 * uno.
 */
export const datosCuentaSchema = z
  .object({
    nombre: nombre.optional(),
    telefono: z
      .string()
      .max(30, "Ese teléfono es demasiado largo.")
      .transform((t) => t.replace(/[\s-]/g, ""))
      .refine((t) => t === "" || /^\+?\d{7,13}$/.test(t), "Escribe un celular válido.")
      .nullable()
      .optional(),
  })
  .refine((d) => d.nombre !== undefined || d.telefono !== undefined, { message: "No llegó nada para cambiar." });

export const entrarSchema = z.object({
  email,
  password: z.string().min(1, "Escribe tu contraseña."),
});

/**
 * ⚠️ **Dos tipos para el mismo esquema, y hacen falta los dos.** `idMunicipio` usa `z.coerce.number()`
 * porque un `<select>` siempre entrega texto: lo que ENTRA es `string`, lo que SALE es `number`. Con un
 * solo tipo, `react-hook-form` no compila — hay que darle el de entrada al formulario y el de salida al
 * `handleSubmit` (su tercer parámetro genérico).
 */
export type RegistroEntrada = z.input<typeof registroSchema>;
export type RegistroInput = z.output<typeof registroSchema>;
export type EntrarInput = z.infer<typeof entrarSchema>;
export type DatosCuentaInput = z.output<typeof datosCuentaSchema>;
